import { OrgRecommendationData } from "../../models/OrgRecommendationData";

// Email templates used to pre-fill the 改善提案メール form.
// Recommendations from the API carry no condition key, so each template is
// matched against the recommendation's title and body text instead.
// Templates are checked in order and the first match wins, so more specific
// patterns (e.g. photo categories) must come before broader ones (photo count).

export interface RecommendationTemplateVariables {
  organizationName?: string;
}

interface RecommendationEmailTemplate {
  conditionKey: string;
  pattern: RegExp;
  subject: string;
  body: (variables: RecommendationTemplateVariables) => string;
}

const header = ({ organizationName }: RecommendationTemplateVariables) =>
  `${organizationName ?? ""}Googleビジネスプロフィール活用支援事務局です。\n` +
  "本メールは、対象となる地域内の施設・店舗の皆さまにお送りしています。\n";

const footer = (topic: string) =>
  `${topic}などのご相談は、本メールへのご返信にてお気軽にお寄せください。`;

const RECOMMENDATION_EMAIL_TEMPLATES: RecommendationEmailTemplate[] = [
  {
    conditionKey: "has_recent_review_reply",
    pattern: /口コミ/,
    subject: "Googleマップの口コミへの返信のおすすめ",
    body: (variables) =>
      header(variables) +
      "Googleマップの口コミへの返信は、投稿されたお客様だけでなく、口コミを読んで来店を検討している方にも見られています。\n\n" +
      "貴施設のプロフィールを拝見したところ、直近30日間に口コミへの返信がない状況でした。\n" +
      "そこで、次のような返信から始めることをおすすめします。\n\n" +
      "・高評価の口コミには、感謝の気持ちを一言\n" +
      "・ご指摘のある口コミには、お詫びと改善への取り組みを丁寧に\n" +
      "・外国語の口コミには、簡単な英語や翻訳ツールを使った返信\n\n" +
      "返信は2〜3行の短い文章で十分です。まずは新しく届いた口コミから返信するだけでも、お客様に安心感を持っていただけます。\n\n" +
      footer("返信の書き方"),
  },
  {
    conditionKey: "has_min_local_categories",
    pattern: /最新情報/,
    subject: "Googleマップの「最新情報」投稿のおすすめ",
    body: (variables) =>
      header(variables) +
      "Googleマップや検索では、来店前に営業時間や最新のお知らせを確認される方が増えています。\n\n" +
      "貴施設のプロフィールを拝見したところ、直近90日間に投稿された「最新情報」が1種類のみ、または投稿がない状況でした。\n" +
      "そこで、次のような投稿を少しずつ増やすことをおすすめします。\n\n" +
      "・お知らせ（営業時間の変更、新商品・新メニューなど）\n" +
      "・イベント（開催日時が決まっているもの）\n" +
      "・特典（期間限定の割引、クーポンなど）\n\n" +
      "1回の投稿は、写真1枚と数行の文章で十分です。まずは月に1回の投稿から始めるだけでも、お客様に見つけてもらいやすくなります。\n\n" +
      footer("投稿の書き方"),
  },
  {
    conditionKey: "has_min_photo_categories",
    pattern: /写真[\s\S]*(カテゴリ|種類)|(カテゴリ|種類)[\s\S]*写真/,
    subject: "Googleマップに掲載する写真の種類を増やすおすすめ",
    body: (variables) =>
      header(variables) +
      "Googleマップや検索では、お客様は写真を見てお店の雰囲気や商品を確認し、来店先を選んでいます。\n\n" +
      "貴施設のプロフィールを拝見したところ、掲載されている写真の種類が4種類未満の状況でした。\n" +
      "そこで、次のような写真をバランスよく追加することをおすすめします。\n\n" +
      "・外観（入口や看板が分かるもの）\n" +
      "・店内・施設内の様子\n" +
      "・商品・メニュー・サービスの様子\n" +
      "・スタッフや作業風景\n\n" +
      "スマートフォンで撮影した写真で十分です。まずは足りない種類の写真を1枚ずつ追加するだけでも、お店の魅力が伝わりやすくなります。\n\n" +
      footer("写真の撮り方・掲載方法"),
  },
  {
    conditionKey: "has_recent_media",
    pattern:
      /(30日|新規|新しい|最近)[\s\S]*(写真|画像)|(写真|画像)[\s\S]*(30日|新規|新しい|最近)/,
    subject: "Googleマップへの新しい写真の追加のおすすめ",
    body: (variables) =>
      header(variables) +
      "Googleマップや検索では、新しい写真が掲載されているお店ほど、今の様子が伝わり安心して来店していただけます。\n\n" +
      "貴施設のプロフィールを拝見したところ、直近30日間に新しい写真が投稿されていない状況でした。\n" +
      "そこで、次のような写真を定期的に追加することをおすすめします。\n\n" +
      "・季節の商品や新メニュー\n" +
      "・店内の飾り付けや季節の風景\n" +
      "・イベントやキャンペーンの様子\n\n" +
      "写真は1枚ずつの追加で十分です。まずは月に1〜2枚の追加から始めるだけでも、お客様に見つけてもらいやすくなります。\n\n" +
      footer("写真の撮り方・掲載方法"),
  },
  {
    conditionKey: "has_min_photo_count",
    pattern: /写真|画像/,
    subject: "Googleマップへの写真掲載のおすすめ",
    body: (variables) =>
      header(variables) +
      "写真は、お客様がGoogleマップや検索でお店を選ぶ際に、最も参考にされる情報のひとつです。\n\n" +
      "貴施設のプロフィールを拝見したところ、掲載されている写真が3枚未満の状況でした。\n" +
      "そこで、次のような写真を掲載することをおすすめします。\n\n" +
      "・外観（入口や看板が分かるもの）\n" +
      "・店内・施設内の様子\n" +
      "・おすすめの商品・メニュー・サービス\n\n" +
      "スマートフォンで撮影した写真で十分です。まずは3枚の掲載から始めるだけでも、お店の雰囲気が伝わりやすくなります。\n\n" +
      footer("写真の撮り方・掲載方法"),
  },
  {
    conditionKey: "has_website_url",
    pattern: /ウェブサイト|Webサイト|ホームページ|URL/i,
    subject: "Googleマップへのウェブサイト登録のおすすめ",
    body: (variables) =>
      header(variables) +
      "Googleマップや検索で施設を見つけた方の多くが、より詳しい情報を求めてウェブサイトやSNSを確認されます。\n\n" +
      "貴施設のプロフィールを拝見したところ、ウェブサイトへのリンクが登録されていない状況でした。\n" +
      "公式サイトをお持ちでない場合も、次のようなページを登録することができます。\n\n" +
      "・公式ホームページ\n" +
      "・Instagram・FacebookなどのSNSアカウント\n" +
      "・予約サイトやオンラインショップのページ\n\n" +
      "登録はGoogleビジネスプロフィールの編集画面から数分で完了します。リンクがあるだけで、お客様が詳しい情報を確認しやすくなります。\n\n" +
      footer("登録方法"),
  },
  {
    conditionKey: "has_primary_phone",
    pattern: /電話/,
    subject: "Googleマップへの電話番号登録のおすすめ",
    body: (variables) =>
      header(variables) +
      "Googleマップや検索では、来店前に電話で営業状況や予約の可否を確認される方も多くいらっしゃいます。\n\n" +
      "貴施設のプロフィールを拝見したところ、電話番号が登録されていない状況でした。\n" +
      "電話番号を登録すると、次のような効果が期待できます。\n\n" +
      "・Googleマップの「電話」ボタンから、お客様がワンタップでお問い合わせできる\n" +
      "・ご予約や在庫確認などの機会を逃しにくくなる\n" +
      "・連絡先が分かることで、お客様に安心感を持っていただける\n\n" +
      "登録はGoogleビジネスプロフィールの編集画面から数分で完了します。番号を変更した際は、あわせて更新をお願いします。\n\n" +
      footer("登録方法"),
  },
  {
    conditionKey: "has_profile_description",
    pattern: /説明|概要|ビジネス情報|プロフィール/,
    subject: "Googleマップの「ビジネス情報（説明文）」記入のおすすめ",
    body: (variables) =>
      header(variables) +
      "Googleマップや検索では、初めてのお客様がお店の特徴やこだわりを説明文で確認されています。\n\n" +
      "貴施設のプロフィールを拝見したところ、ビジネスの説明文が未記入の状況でした。\n" +
      "そこで、次のような内容を数行でまとめることをおすすめします。\n\n" +
      "・どのようなお店・施設か（業種、取り扱う商品やサービス）\n" +
      "・こだわりやおすすめのポイント\n" +
      "・利用シーンやアクセス（ご家族連れに人気、駅から徒歩5分 など）\n\n" +
      "説明文は100〜200文字程度で十分です。一度記入しておくだけで、お客様にお店の魅力が伝わりやすくなります。\n\n" +
      footer("説明文の書き方"),
  },
  {
    conditionKey: "has_primary_category_service_types",
    pattern: /商品|サービス/,
    subject: "Googleマップへの商品・サービス掲載のおすすめ",
    body: (variables) =>
      header(variables) +
      "Googleマップや検索では、どのような商品・サービスがあるかを比較して来店先を選ぶ方が増えています。\n\n" +
      "貴施設のプロフィールを拝見したところ、商品またはサービスが登録されていない状況でした。\n" +
      "そこで、次のような内容を登録することをおすすめします。\n\n" +
      "・主力の商品やメニュー\n" +
      "・提供しているサービス（テイクアウト、修理、体験プランなど）\n" +
      "・価格の目安\n\n" +
      "まずは代表的なものを3つほど登録するだけでも、お客様が来店前にイメージしやすくなります。\n\n" +
      footer("登録方法"),
  },
];

export const getRecommendationEmailTemplate = (
  recommendation: Pick<OrgRecommendationData, "title" | "body">,
  variables: RecommendationTemplateVariables
): { messageTitle: string; messageText: string } | undefined => {
  const text = `${recommendation.title ?? ""}\n${recommendation.body ?? ""}`;
  const template = RECOMMENDATION_EMAIL_TEMPLATES.find(({ pattern }) =>
    pattern.test(text)
  );

  if (!template) {
    return undefined;
  }

  return {
    messageTitle: template.subject,
    messageText: template.body(variables),
  };
};
